import { ArrowLeftIcon, FileTextIcon } from '@phosphor-icons/react';
import { PermissionEnum } from '@sr-sdks/api-sdk-axios';
import {
  SR_ICONS,
  Sheet,
  SheetProps,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  cn,
  useIsMobile,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../api/task/taskApi';
import { queryOptionsGetWorkspacesPaginated } from '../api/workspaces/workspaceApi';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useExcludedTaskCounts } from '../hooks/useExcludedTaskCounts';
import { useRoyaltiesRouteFlags } from '../hooks/useRoyaltiesRouteFlags';
import { SidebarVariant } from '../types/sidebar';
import { ManageWorkspacePermissions } from '../types/workspaceTypes';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { IfFeatureEnabledMSR3 } from './IfFeatureEnabledMSR3';
import { MigrationSwitch } from './MigrationSwitch';
import ProtectElement from './ProtectElement';
import { MultipleQueryResults } from './QueryResults/MultipleQueryResults';
import { SIDEBAR_LINK_CLASS, SidebarLink } from './SidebarLink';
import { SidebarLinkGroup } from './SidebarLinkGroup';
import { WorkspaceSelectorMSR3 } from './WorkspaceSelectorMSR3';
import { UserProfileDropdown } from './navigation/UserProfileDropdown';

const SidebarShell = ({
  children,
  logoHref,
}: {
  children: ReactNode;
  logoHref: string;
}) => (
  // SidebarMenuButton (used by SidebarLink/SidebarLinkGroup) calls useSidebar(),
  // so a SidebarProvider must exist in the tree. We only need its context +
  // TooltipProvider, not its layout — make it a plain full-height block so the
  // inner `h-full` shell keeps filling the nav (this is what lets `grow` on the
  // menu push the profile dropdown to the bottom). `block` drops the provider's
  // default `flex` (which would shrink the shell to content width); `h-full
  // min-h-0` replaces its `min-h-svh`.
  <SidebarProvider className="block h-full min-h-0 w-full">
    <div className="flex h-full flex-col gap-6 bg-white pb-3 pt-6">
      <Link className="w-[110px] max-w-[80%] self-center" to={logoHref}>
        <img alt="Screenrights logo" src="/screenrights-logo.png" />
      </Link>
      {children}
      <div className="mt-auto px-2">
        <UserProfileDropdown />
      </div>
    </div>
  </SidebarProvider>
);

/**
 * `/` resolves to `workspaces.items[0]` -- another customer's workspace for
 * staff -- so the URL's id stands in until the query holds real data for it.
 * Not `isPending` alone: the query keeps previous data, so a switch sits at
 * status success holding the workspace we just left. Once resolved empty we are
 * on the not-found page, where that id is dead and `/` is the only escape.
 */
const useWorkspaceHomepageUrl = () => {
  const [activeWorkspace, activeWorkspaceQuery] = useActiveWorkspace();
  const { workspaceId } = useParams();

  const hasResolved =
    !activeWorkspaceQuery.isPending && !activeWorkspaceQuery.isPlaceholderData;

  const id = hasResolved ? activeWorkspace?._id : workspaceId;

  return id ? `/workspace/${id}/home` : '/';
};

/**
 * Tasks nav entry with a badge showing the total task count for the workspace.
 * The count is deliberately unfiltered (no search term / type) so it reflects
 * every task, less each tab's `countExcludedStatuses` (resolved competing
 * claims), matching the tab badges on the Tasks page. Only mounted when the
 * tasks route flag is on, so the count queries never fire otherwise.
 */
const TasksSidebarLink = ({
  basePath,
  workspaceId,
}: {
  basePath: string;
  workspaceId?: string;
}) => {
  const { data } = useQuery({
    ...queryOptionsGetTasksPaginated({ pageSize: 1, workspaceId }),
    enabled: Boolean(workspaceId),
  });

  const excludedCounts = useExcludedTaskCounts(workspaceId);

  const taskCount =
    data && excludedCounts
      ? (data.meta?.totalItems ?? 0) -
        [...excludedCounts.values()].reduce((sum, count) => sum + count, 0)
      : undefined;

  return (
    <SidebarLink
      label="Tasks"
      badge={taskCount ? taskCount : undefined}
      icon={SR_ICONS.filmStrip}
      iconClassName="rotate-90"
      isPrefetching
      to={`${basePath}/tasks`}
    />
  );
};

/**
 * `logoHref` overrides the workspace-derived destination, for shells rendered on
 * a workspace URL the user has just been refused -- linking back there loops.
 */
type SidebarContentProps = {
  logoHref?: string;
};

/**
 * Workspace sidebar - logo, workspace selector, workspace navigation links,
 * and user profile dropdown.
 */
const SidebarContent = ({
  logoHref: logoHrefOverride,
}: SidebarContentProps) => {
  const [activeWorkspace, activeWorkspaceQuery] = useActiveWorkspace();
  const paginatedWorkspacesQuery = useQuery(
    queryOptionsGetWorkspacesPaginated(),
  );
  // Each show* flag is route-flag AND access, so nav never offers a link the
  // route guard would answer with not-found. All three are false while
  // membership is resolving, so links do not flash on a stale membershipStatus.
  const {
    isEarningsStatementsGroup,
    isLoadingMembership,
    showDisbursementsNav,
    showResidualsNav,
    showRoyaltiesAnzNav,
    showRoyaltiesNav,
    showRoyaltiesWorldNav,
  } = useRoyaltiesRouteFlags();
  const workspaceHomepageUrl = useWorkspaceHomepageUrl();
  const logoHref = logoHrefOverride ?? workspaceHomepageUrl;

  const basePath = activeWorkspace?._id
    ? `/workspace/${activeWorkspace._id}`
    : '/workspace';

  const homepageUrl = `${basePath}/home`;

  const paymentsNavLink = (
    <SidebarLink
      label="Payments"
      icon={SR_ICONS.currencyCircleDollar}
      isPrefetching
      to={`${basePath}/payments`}
    />
  );

  const statementsGroupLabel = isEarningsStatementsGroup
    ? 'Earnings Statements'
    : 'Statements';
  const royaltiesNavLink = (
    <SidebarLink
      label={isEarningsStatementsGroup ? 'Historic Royalties' : 'Royalties'}
      isPrefetching
      to={`${basePath}/royalties`}
    />
  );

  const statementsMatchPaths = [
    ...(showDisbursementsNav ? ['/disbursements'] : []),
    ...(showResidualsNav ? ['/residuals'] : []),
    ...(showRoyaltiesNav ? ['/royalties'] : []),
    ...(showRoyaltiesAnzNav ? ['/royalties-anz'] : []),
    ...(showRoyaltiesWorldNav ? ['/royalties-world'] : []),
  ];

  const hasStatementLinks = statementsMatchPaths.length > 0;

  return (
    <SidebarShell logoHref={logoHref}>
      <MultipleQueryResults
        queries={{ activeWorkspaceQuery, paginatedWorkspacesQuery }}
      >
        <div className="px-2">
          <WorkspaceSelectorMSR3 />
        </div>

        {/* Keep flex-grow: 1 to dynamically expand to full height */}
        <SidebarMenu className="grow gap-0 px-4">
          <SidebarLink
            label="Home"
            icon={SR_ICONS.house}
            isPrefetching
            to={homepageUrl}
          />

          <ProtectElement
            permissionRequired={[PermissionEnum.ViewTitles]}
            workspaceId={activeWorkspace?._id}
          >
            <SidebarLink
              label="Titles"
              icon={SR_ICONS.filmStrip}
              iconClassName="rotate-90"
              isPrefetching
              to={`${basePath}/titles`}
            />
          </ProtectElement>

          {workspaceTypeIsAgent(activeWorkspace) && (
            <ProtectElement
              permissionRequired={[PermissionEnum.ViewClients]}
              workspaceId={activeWorkspace?._id}
            >
              <SidebarLink
                label="Clients"
                icon={SR_ICONS.userCircle}
                isPrefetching
                to={`${basePath}/clients`}
              />
            </ProtectElement>
          )}

          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE}
            loadingFallback={paymentsNavLink}
            newComponent={
              <SidebarLinkGroup
                label={statementsGroupLabel}
                icon={FileTextIcon}
                matchPaths={statementsMatchPaths}
              >
                {showRoyaltiesAnzNav && (
                  <SidebarLink
                    label="Royalties ANZ"
                    isPrefetching
                    to={`${basePath}/royalties-anz`}
                  />
                )}

                {showRoyaltiesWorldNav && (
                  <SidebarLink
                    label="Royalties World"
                    isPrefetching
                    to={`${basePath}/royalties-world`}
                  />
                )}

                {/* Historic Royalties moves below Disbursements/Residuals only
                    once an ANZ or World link actually renders above it
                    — isEarningsStatementsGroup is access-aware, not a flag OR,
                    so the group never renames with nothing above it. */}
                {!isEarningsStatementsGroup &&
                  showRoyaltiesNav &&
                  royaltiesNavLink}

                {showDisbursementsNav && (
                  <SidebarLink
                    label="Disbursements"
                    isPrefetching
                    to={`${basePath}/disbursements`}
                  />
                )}

                {showResidualsNav && (
                  <SidebarLink
                    label="Residuals"
                    isPrefetching
                    to={`${basePath}/residuals`}
                  />
                )}

                {isEarningsStatementsGroup &&
                  showRoyaltiesNav &&
                  royaltiesNavLink}

                {/* Royalties links are all false while membership resolves,
                    so wait for it before saying there is nothing here. */}
                {!hasStatementLinks && !isLoadingMembership && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      className={cn(
                        SIDEBAR_LINK_CLASS,
                        'cursor-default text-sm font-light text-foreground/70 hover:text-foreground/70',
                      )}
                    >
                      <span aria-disabled="true" data-sidebar-link>
                        No statements available
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarLinkGroup>
            }
            oldComponent={paymentsNavLink}
          />

          <IfFeatureEnabledMSR3
            name={FEATURE_FLAGS.MSR3_TASKS_ROUTE}
            isEnabled="true"
          >
            <TasksSidebarLink
              basePath={basePath}
              workspaceId={activeWorkspace?._id}
            />
          </IfFeatureEnabledMSR3>

          <ProtectElement
            permissionRequired={ManageWorkspacePermissions}
            workspaceId={activeWorkspace?._id}
          >
            <SidebarLink
              label="Settings"
              icon={SR_ICONS.gear}
              isPrefetching
              to={`${basePath}/manage-workspace`}
            />
          </ProtectElement>
        </SidebarMenu>
      </MultipleQueryResults>
    </SidebarShell>
  );
};

/**
 * Profile sidebar keeps the MSR3 shell while removing workspace-only controls.
 */
const ProfileSidebarContent = ({
  logoHref: logoHrefOverride,
}: SidebarContentProps) => {
  const workspaceHomepageUrl = useWorkspaceHomepageUrl();
  const navigate = useNavigate();

  return (
    <SidebarShell logoHref={logoHrefOverride ?? workspaceHomepageUrl}>
      <SidebarMenu className="grow gap-0 px-4">
        <SidebarLink
          label="Go Back"
          icon={ArrowLeftIcon}
          onClick={() => navigate(-1)}
        />
      </SidebarMenu>
    </SidebarShell>
  );
};

/**
 * Responsive Sidebar component.
 *
 * - Desktop (md and above): Renders as a fixed sidebar
 * - Mobile (below md): Renders as a Sheet/slide-out panel from the left
 *
 * For mobile, the sidebar requires `open` and `onOpenChange` props to control visibility.
 */
type SidebarProps = Omit<SheetProps, 'heading'> &
  SidebarContentProps & {
    variant?: SidebarVariant;
  };

const renderSidebarContent = (variant: SidebarVariant, logoHref?: string) =>
  variant === 'profile' ? (
    <ProfileSidebarContent logoHref={logoHref} />
  ) : (
    <SidebarContent logoHref={logoHref} />
  );

export const Sidebar = ({
  logoHref,
  variant = 'workspace',
  ...props
}: SidebarProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet
        heading="Navigation Menu"
        className="single-customer-app w-full p-0"
        hasFooter={false}
        hasHeader={false}
        side="left"
        {...props}
      >
        {/* Only mount the content (and its query/hook cost) when the sheet
            is actually open. */}
        {props.open && renderSidebarContent(variant, logoHref)}
      </Sheet>
    );
  }

  // Desktop view
  return (
    <nav className="single-customer-app relative hidden h-screen w-full max-w-[17rem] bg-white md:block">
      {renderSidebarContent(variant, logoHref)}

      {/* Gradient border */}
      <div
        className="pointer-events-none absolute right-0 top-0 z-0 h-full w-1"
        style={{
          background:
            'linear-gradient(to bottom, #8b5cf6, #3b82f6, #10b981, #f59e0b, #ef4444)',
          clipPath: 'ellipse(1px 49% at 98% 50%)',
          maskImage:
            'radial-gradient(circle, black 0%, black 20%, transparent 110%)',
        }}
      />
    </nav>
  );
};
